import i18n from '@/i18nSetup';
import module from '@/models/AxiosModule';

function transformToAssocArray(prmstr) {
  var params = {};
  var prmarr = prmstr.split("&");
  for (var i = 0; i < prmarr.length; i++) {
    var tmparr = prmarr[i].split("=");
    params[tmparr[0]] = tmparr[1];
  }
  return params;
}

function getSearchParameters() {
  var prmstr = window.location.search.substr(1);
  return prmstr != null && prmstr != "" ? transformToAssocArray(prmstr) : {};
}

async function changeLanguage(lang, getProfile) {
  i18n.locale = lang;
  localStorage.setItem('userLanguage', lang);
  
  // Update the document title based on the language
  const titles = {
    'en': 'Lucmus - Social Media Pages Monitoring Tool',
    'ru': 'Lucmus - Сервис аналитики и мониторинга страниц социальных сетей',
    'es': 'Lucmus - Herramienta de monitoreo de páginas de redes sociales',
    'pt': 'Lucmus - Ferramenta de monitoramento de páginas de redes sociais',
    'de': 'Lucmus - Sozialmedien-Seiten-Überwachungstool'
  };
  document.title = titles[lang] || titles['en'];
  
  try {
    const axios = module.Axios;
    const response = await axios.post("set-locale", {
      email: getProfile.email,
      locale: lang.toLowerCase()
    });
  } catch (error) {
  }      
}

async function exportChartToSVG({
  chart,
  renderFn,
  container,
  renderOptions,
  revertFn,
  filename = `chart_${new Date().toISOString().split('T')[0]}`
}) {
  if (!chart || !container) {
    return;
  }
  
  try {
    if (chart.dispose) chart.dispose();
    const svgOptions = {
      ...renderOptions,
      renderer: 'svg',
      animation: false
    };
    
    const svgChart = await renderFn(svgOptions);
    const svgContent = container.querySelector('svg').outerHTML;
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    
    downloadFile(blob, `${filename}.svg`);
    
    if (svgChart.dispose) svgChart.dispose();
    await revertFn();
    
  } catch (error) {
    
    // Try to revert back in case of error
    await revertFn();
  }
}

function exportChartToCSV({
  chart,
  seriesNames = [],
  dataExtractor,
  filename = `chart_data_${new Date().toISOString().split('T')[0]}`
}) {
  if (!chart) {
    return;
  }

  try {
    let csvContent;
    
    if (typeof dataExtractor === 'function') {
      csvContent = dataExtractor(chart);
    } else {
      const option = chart.getOption();
      if (!option || !option.series) {
        return;
      }

      const series = option.series || [];
      const categories = (option.xAxis && option.xAxis[0] && option.xAxis[0].data) || [];
      
      // Create header row
      csvContent = "Date";
      
      if (seriesNames.length > 0) {
        // Use provided series names
        seriesNames.forEach(name => {
          csvContent += `;${name}`;
        });
      } else {
        // Use series names from chart if available
        series.forEach(s => {
          csvContent += `;${s.name || 'Series'}`;
        });
      }
      csvContent += "\n";
      
      // Create data rows
      categories.forEach((category, index) => {
        // Add the category (date) as the first column
        csvContent += `"${category}"`;
        
        // Add values from each series
        series.forEach(seriesItem => {
          let value = 0;
          
          // Handle different data formats
          if (seriesItem.data && seriesItem.data[index]) {
            const dataPoint = seriesItem.data[index];
            if (Array.isArray(dataPoint)) {
              value = dataPoint[1]; // [x,y] format
            } else if (typeof dataPoint === 'object' && dataPoint.value !== undefined) {
              value = dataPoint.value;
            } else {
              value = dataPoint;
            }
          }
          
          csvContent += `;${formatCsvValue(value)}`;
        });
        
        csvContent += "\n";
      });
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    
    downloadFile(blob, `${filename}.csv`);
    
  } catch (error) {
  }
}

function downloadFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function exportStackedChart({
  chart,
  container,
  chartData,
  activeCells,
  activeColors,
  period,
  chartFn,
  type = 'svg'
}) {
  if (!chart || !container) {
    return;
  }

  const { pages, datesRangePosts, datesRangeStories } = chartData;
  const filename = `stacked_chart_${type === 'csv' ? 'data_' : ''}${new Date().toISOString().split('T')[0]}`;

  if (type === 'svg') {
    await exportChartToSVG({
      chart,
      container,
      renderFn: async (options) => chartFn({
        pages,
        datesRangePosts,
        datesRangeStories,
        names: activeCells,
        ref: container,
        colors: activeColors,
        period,
        ...options
      }),
      revertFn: async () => {
        chart = await chartFn({
          pages,
          datesRangePosts,
          datesRangeStories,
          names: activeCells,
          ref: container,
          colors: activeColors,
          period,
          renderer: 'canvas',
          animation: true
        });
      },
      filename
    });
  } else if (type === 'csv') {
    exportChartToCSV({
      chart,
      seriesNames: activeCells,
      filename
    });
  }
}

async function initializeChart({ chartFn, data, container, config = {} }) {
  if (!chartFn || !container) {
    return null;
  }

  try {
    return await chartFn({
      ...data,
      ref: container,
      ...config
    });
  } catch (error) {
    return null;
  }
}

function exportDataToCSV({
  series,
  categories,
  seriesNames = [],
  filename = `chart_data_${new Date().toISOString().split('T')[0]}`
}) {
  if (!series || !categories) {
    return;
  }

  try {
    let csvContent = "Date";
    const headerNames = seriesNames.length > 0 ? seriesNames : series.map(s => s.name || 'Series');
    headerNames.forEach(name => {
      csvContent += `;${name}`;
    });
    csvContent += "\n";
    
    categories.forEach((category, index) => {
      csvContent += `"${category}"`;
      
      series.forEach(seriesItem => {
        let value = 0;
        if (seriesItem.data && seriesItem.data[index] !== undefined) {
          value = seriesItem.data[index];
        }
        else if (seriesItem.data && Array.isArray(seriesItem.data[index])) {
          value = seriesItem.data[index][1]; // [date, value] format
        }
        
        csvContent += `;${formatCsvValue(value)}`;
      });
      
      csvContent += "\n";
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    
    downloadFile(blob, `${filename}.csv`);
    
  } catch (error) {
  }
}


function formatCsvValue(value) {
  if (typeof value === 'number' || !isNaN(parseFloat(value))) {
    const strValue = String(value);
    return strValue.replace('.', ',');
  }
  return value;
}

export default {
  getSearchParameters,
  changeLanguage,
  exportChartToSVG,
  exportChartToCSV,
  downloadFile,
  exportStackedChart,
  initializeChart,
  exportDataToCSV,
  formatCsvValue
}